import { useParams, useNavigate } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ChevronLeft, Loader2, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { trpcClient } from "@/lib/trpc";
import TechnicalSpecForm, {
  type TechnicalSpecFormData,
} from "./TechnicalSpecForm";

export default function TechnicalSpecEdit() {
  const { specId } = useParams({ strict: false }) as { specId: string };
  const navigate = useNavigate();
  const qc = useQueryClient();

  const {
    data: spec,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["technicalSpec", specId],
    queryFn: () => trpcClient.technical.specs.getById.query({ id: specId }),
  });

  const updateMutation = useMutation({
    mutationFn: async (data: TechnicalSpecFormData) => {
      if (!spec) throw new Error("No spec data");
      return trpcClient.technical.specs.update.mutate({
        id: spec.id,
        data: data as any,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["technicalSpec", specId] });
      qc.invalidateQueries({ queryKey: ["technical-specs"] });
      navigate({ to: `/specs/${specId}` as any });
    },
    onError: (error: any) => {
      toast.error(error.message || "Cập nhật thất bại");
    },
  });

  const handleSubmit = async (data: TechnicalSpecFormData) => {
    await updateMutation.mutateAsync(data);
  };

  if (isLoading) {
    return (
      <div className="flex h-[calc(100vh-64px)] items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-indigo-600" />
          <p className="text-sm text-slate-500">Đang tải TSKT...</p>
        </div>
      </div>
    );
  }

  if (error || !spec) {
    return (
      <div className="flex h-[calc(100vh-64px)] items-center justify-center p-4">
        <Card className="max-w-md p-6 text-center shadow-sm">
          <AlertCircle className="mx-auto h-12 w-12 text-rose-500 mb-4" />
          <h3 className="mb-2 text-lg font-semibold text-slate-900">
            Không tìm thấy hoặc lỗi
          </h3>
          <p className="mb-6 text-sm text-slate-500">
            {error ? error.message : "TSKT này không tồn tại hoặc đã bị xóa."}
          </p>
          <Button
            onClick={() => navigate({ to: "/specs" })}
            variant="outline"
            className="w-full"
          >
            <ChevronLeft className="mr-2 h-4 w-4" /> Về danh sách
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="w-full space-y-4 p-4 animate-in fade-in duration-300">
      <div className="mb-6 flex items-center gap-4">
        <Button
          variant="outline"
          size="icon"
          onClick={() => navigate({ to: `/specs/${specId}` as any })}
          className="h-8 w-8"
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <div>
          <p className="text-sm text-slate-500">
            Cập nhật thông tin cho {spec.specId}
          </p>
        </div>
      </div>

      <TechnicalSpecForm
        defaultValues={{
          customerId: spec.product?.customerId || "",
          productId: spec.productId || "",
          productName: spec.productName || spec.product?.productName || "",
          status: spec.status || "DRAFT",
          version: spec.version || "1.0",

          materialId: spec.product?.materialId || "",
          packingSpec: spec.product?.packingSpec || "",
          process: spec.product?.process || "",

          specDetails: (spec.finishedSize as any) || {},

          notes: spec.notes || "",
        }}
        onSubmit={handleSubmit}
        onCancel={() => navigate({ to: `/specs/${specId}` as any })}
        isSubmitting={updateMutation.isPending}
        submitLabel="Lưu cập nhật"
      />
    </div>
  );
}
